import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { FileText, Shield, Zap, BarChart3, Receipt, Clock, Building2, Globe, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function WhatIsMalaysiaEInvoice() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="What is Malaysia E-Invoice? Guide 2026 | StayMii"
        description="Complete 2026 guide to Malaysia's e-invoice system. Learn what e-invoicing is, how LHDN's MyInvois works, who must comply, key deadlines, penalties, and how to automate compliance with StayMii."
        keywords="malaysia e-invoice guide, what is e-invoice malaysia, malaysia e-invoice 2026, einvoice malaysia explained, lhdn e-invoice guide, myinvois guide, e-invoice malaysia for beginners, malaysia digital invoice"
        canonicalUrl="/what-is-malaysia-einvoice"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="Malaysia E-Invoice Guide 2026"
          h1="What is Malaysia E-Invoice? The Complete 2026 Guide"
          subtitle="Malaysia's e-invoice system, managed by LHDN through the MyInvois platform, requires all businesses to submit invoices digitally. This comprehensive guide explains what e-invoicing is, who must comply, key deadlines, and how to get started."
        />

        <SEOSection h2="What is E-Invoice?" description="E-invoice (electronic invoice) is a digital document that records a commercial transaction between a seller and buyer. In Malaysia, e-invoices must be created, submitted, and validated through LHDN's MyInvois system before they become legally valid.">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">E-Invoice vs Traditional Invoice</h3>
              <div className="space-y-4">
                <div className="border-b border-slate-200 pb-4">
                  <p className="text-sm font-semibold text-slate-800">Traditional Invoice</p>
                  <p className="text-sm text-slate-500">Paper or PDF document created by the business. No government validation. Stored locally. Error-prone and difficult to audit.</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">E-Invoice (MyInvois)</p>
                  <p className="text-sm text-slate-500">Digital document submitted to LHDN via API. Validated in real-time by the government. Centrally stored and audit-ready. Reduces tax fraud and improves compliance.</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0F172A] rounded-2xl p-8 border border-blue-500/20">
              <h3 className="text-lg font-bold text-white mb-4">Key Facts About Malaysia E-Invoice</h3>
              <div className="space-y-4">
                {[
                  'Managed by LHDN (Lembaga Hasil Dalam Negeri) — Malaysia\'s Inland Revenue Board',
                  'Submitted through the MyInvois system via API or MyInvois portal',
                  'Every e-invoice receives a unique LHDN validation number',
                  'Covers invoices, credit notes, debit notes, and refund notes',
                  'Mandatory for all businesses — phased rollout based on revenue',
                  'Non-compliance penalties up to RM200,000 or imprisonment',
                  'Applies to B2B and B2C transactions',
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-white/80">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SEOSection>

        <SEOSection h2="Who Must Comply With E-Invoice in Malaysia?" bg="bg-slate-50" description="LHDN's e-invoice mandate applies to all taxpayers conducting commercial activities in Malaysia. The rollout is phased based on annual revenue.">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { phase: 'Phase 1', date: 'August 2024', criteria: 'Annual revenue > RM100 million', status: 'Active' },
              { phase: 'Phase 2', date: 'January 2025', criteria: 'Annual revenue > RM25 million', status: 'Active' },
              { phase: 'Phase 3', date: 'July 2025', criteria: 'All remaining businesses', status: 'Active' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-blue-50 text-[#2563EB] text-xs font-bold rounded-full">{item.phase}</span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full">{item.status}</span>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{item.date}</h3>
                <p className="text-sm text-slate-600">{item.criteria}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Important: As of July 2025, ALL businesses in Malaysia must comply with e-invoice requirements.</p>
                <p className="text-sm text-amber-700 mt-1">This includes sole proprietors, partnerships, companies, and individuals earning income from business activities. If you haven't started yet, take action now to avoid penalties.</p>
              </div>
            </div>
          </div>
        </SEOSection>

        <SEOSection h2="How Does MyInvois E-Invoice Work?" description="The MyInvois system is LHDN's platform for receiving, validating, and storing e-invoices. Here's the step-by-step process:">
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { step: '1', title: 'Create Invoice', desc: 'Business creates an invoice with all required fields (buyer info, items, taxes, amounts).' },
              { step: '2', title: 'Submit to LHDN', desc: 'Invoice is submitted to MyInvois via API or portal in the required XML/JSON format.' },
              { step: '3', title: 'LHDN Validates', desc: 'MyInvois validates the invoice structure, tax calculations, and mandatory fields.' },
              { step: '4', title: 'Unique ID Issued', desc: 'If valid, LHDN assigns a unique validation number and QR code to the e-invoice.' },
              { step: '5', title: 'Share & Store', desc: 'The validated e-invoice can be shared with the buyer and is stored in the MyInvois system.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 bg-[#2563EB] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold text-sm">{item.step}</span>
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-xs text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="What Information Must an E-Invoice Contain?" bg="bg-slate-50">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Seller Information', desc: 'Business name, TIN (Tax Identification Number), registration number, address, and contact details.' },
              { title: 'Buyer Information', desc: 'For B2B: buyer TIN, name, registration number, address. For B2C: general public designation or buyer details if provided.' },
              { title: 'Invoice Details', desc: 'Unique invoice number, issue date, currency (MYR), and invoice type (invoice, credit note, debit note).' },
              { title: 'Line Items', desc: 'Description, quantity, unit price, discount (if any), and total amount for each item or service.' },
              { title: 'Tax Information', desc: 'Tax type (SST, service tax, tourism tax), tax rate, tax amount per line item, and total tax amount.' },
              { title: 'Totals', desc: 'Subtotal, total tax, total discount, and grand total amount payable.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-200 h-full">
                <h3 className="text-base font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="Penalties for Non-Compliance" description="LHDN takes e-invoice compliance seriously. Businesses that fail to comply with the e-invoice mandate face severe consequences.">
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-base font-bold text-red-800 mb-2">Financial Penalties</h3>
              <p className="text-sm text-red-700">Fines of up to RM200,000 for failure to comply with e-invoice requirements. Repeated violations may result in higher penalties.</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-base font-bold text-red-800 mb-2">Criminal Penalties</h3>
              <p className="text-sm text-red-700">Imprisonment of up to 6 months for serious or wilful non-compliance. Directors and officers may be personally liable.</p>
            </div>
          </div>
        </SEOSection>

        <SEOSection h2="How StayMii Automates E-Invoice Compliance" bg="bg-slate-50">
          <FeatureGrid items={[
            { icon: Zap, title: 'Automatic Invoice Generation', desc: 'StayMii generates e-invoices automatically from your PMS bookings, sales, and transactions. No manual data entry needed.' },
            { icon: Shield, title: 'Pre-Submission Validation', desc: 'Every invoice is validated against LHDN specifications before submission, minimising rejections and errors.' },
            { icon: Globe, title: 'MyInvois API Integration', desc: 'Direct API connection to LHDN MyInvois. Invoices are submitted and validated in real-time.' },
            { icon: Receipt, title: 'Credit Notes & Debit Notes', desc: 'Handle adjustments, cancellations, and refunds with proper CN/DN documentation as required by LHDN.' },
            { icon: BarChart3, title: 'Compliance Dashboard', desc: 'Real-time visibility into submission status, rejection reasons, and overall compliance health.' },
            { icon: Clock, title: 'Quick Setup', desc: 'Go live in 2-3 days. StayMii handles the technical complexity of MyInvois integration for you.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Frequently Asked Questions About Malaysia E-Invoice">
          <div className="space-y-6 max-w-3xl">
            {[
              { q: 'Is e-invoice mandatory for all businesses in Malaysia?', a: 'Yes, as of July 2025. All businesses in Malaysia, regardless of size or revenue, must comply with LHDN\'s e-invoice requirements. This includes sole proprietors, SMEs, and large corporations.' },
              { q: 'What is MyInvois?', a: 'MyInvois is LHDN\'s official platform for receiving and validating e-invoices. Businesses submit invoices to MyInvois via API or the MyInvois web portal. Each validated invoice receives a unique ID and QR code from LHDN.' },
              { q: 'Can I still issue paper invoices?', a: 'You must submit an e-invoice through MyInvois for every transaction. However, you can still provide paper or PDF copies to your customers alongside the e-invoice. The e-invoice is the legally valid document.' },
              { q: 'What about B2C transactions (sales to individuals)?', a: 'B2C e-invoices can use "General Public" as the buyer designation if the individual customer does not provide their TIN. LHDN also allows consolidated B2C invoicing for certain transaction types.' },
              { q: 'Do I need special software for e-invoicing?', a: 'You can submit manually through the MyInvois portal, but this is impractical for businesses with volume transactions. E-invoice software like StayMii automates the entire process and is strongly recommended.' },
              { q: 'How does StayMii help with e-invoice compliance?', a: 'StayMii connects to your PMS or booking system, automatically generates LHDN-compliant e-invoices, validates them, and submits them to MyInvois via API. You get a dashboard to track compliance status in real-time.' },
            ].map((faq, i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-6">
                <h3 className="text-base font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="Related Guides & Solutions" bg="bg-slate-50">
          <InternalLinkGrid links={[
            { to: '/lhdn-einvoice-guide-hotels', title: 'LHDN E-Invoice Guide for Hotels', desc: 'Specific guidance for hotel and hospitality businesses' },
            { to: '/malaysia-einvoice-software', title: 'Malaysia E-Invoice Software', desc: 'Automated e-invoice software for Malaysian businesses' },
            { to: '/e-invoice-malaysia', title: 'E-Invoice Malaysia Overview', desc: 'Detailed overview of Malaysia\'s e-invoice ecosystem' },
            { to: '/hotel-einvoice-system-malaysia', title: 'Hotel E-Invoice System', desc: 'E-invoicing built specifically for hotels' },
            { to: '/lhdn-e-invoice', title: 'LHDN E-Invoice Compliance', desc: 'LHDN compliance requirements and how to meet them' },
            { to: '/myinvois-api', title: 'MyInvois API Integration', desc: 'Technical guide to MyInvois API connectivity' },
          ]} />
        </SEOSection>

        <CTABanner title="Don't Wait Until It's Too Late" subtitle="E-invoice compliance is mandatory for all Malaysian businesses. Get started with StayMii today and automate your compliance." />
        <LandingFooter />
      </div>
    </>
  );
}
